<template>
  <section
    class="sec reveal"
    id="trust"
    data-ga-section="security"
  >
    <div class="trust-grid">
      <div class="trust-vis">
        <div class="safe-heart">
          <icon-safe-heart />
          <div
            class="sh-lock"
            aria-hidden="true"
          >
            <span class="sh-shackle"></span>
            <span class="sh-body"><span class="sh-keyhole"></span></span>
          </div>
        </div>
        <div class="badge-float b1">
          <div class="ic">
            <icon-lock-outline
              :size="14"
              :stroke-width="2"
            />
          </div>
          {{ trans.get('landing.trust.badge_dsgvo') }}
        </div>
        <div class="badge-float b2">
          <div class="ic ic--encrypted">
            <icon-check
              :size="14"
              :stroke-width="3"
            />
          </div>
          {{ trans.get('landing.trust.badge_encrypted') }}
        </div>
        <div class="badge-float b3">
          <div class="ic ic--privacy">
            <span class="ic-bg"></span>
            <span class="ic-veil"></span>
            <icon-lock-badge class="ic-lock" />
          </div>
          {{ trans.get('landing.trust.badge_privacy') }}
        </div>
      </div>

      <div class="trust-text">
        <span class="sec-eyebrow">{{ trans.get('landing.trust.eyebrow') }}</span>
        <h2 class="sec-title">
          {{ trans.get('landing.trust.title_line1') }}
          {{ trans.get('landing.trust.title_line2_prefix')
          }}<span class="grad">{{ trans.get('landing.trust.title_line2_grad') }}</span
          ><span class="title-dot">{{ trans.get('landing.trust.title_line2_suffix') }}</span>
        </h2>
        <p class="sec-lede sec-lede--tight">{{ trans.get('landing.trust.lede') }}</p>

        <div class="trust-list">
          <div class="trust-item">
            <div class="ic">
              <icon-check-circle
                :size="20"
                :stroke-width="2"
              />
            </div>
            <div>
              <h4>{{ trans.get('landing.trust.item1_title') }}</h4>
              <p>{{ trans.get('landing.trust.item1_text') }}</p>
            </div>
          </div>
          <div class="trust-item">
            <div class="ic">
              <icon-lock-outline
                :size="20"
                :stroke-width="2"
              />
            </div>
            <div>
              <h4>{{ trans.get('landing.trust.item2_title') }}</h4>
              <p>{{ trans.get('landing.trust.item2_text') }}</p>
            </div>
          </div>
          <div class="trust-item">
            <button
              class="ic privacy-ic"
              type="button"
              :aria-pressed="photoRevealed ? 'true' : 'false'"
              :aria-label="trans.get('landing.trust.photo_aria')"
              :class="{ revealed: photoRevealed }"
              @click="photoRevealed = !photoRevealed"
            >
              <div class="pi-photos">
                <div class="pi-photo pi-photo--lena"></div>
                <div class="pi-photo pi-photo--sophia"></div>
                <div class="pi-photo pi-photo--man1"></div>
                <div class="pi-photo pi-photo--mia"></div>
              </div>
              <div class="pi-veil"></div>
              <span class="pi-lock pi-lock-closed"
                ><icon-lock-closed
                  :size="18"
                  :stroke-width="2.4"
              /></span>
              <span class="pi-lock pi-lock-open"
                ><icon-lock-open
                  :size="18"
                  :stroke-width="2.4"
              /></span>
            </button>
            <div>
              <h4>{{ trans.get('landing.trust.item3_title') }}</h4>
              <p>
                {{ trans.get('landing.trust.item3_text_prefix')
                }}<strong>{{ trans.get('landing.trust.item3_text_strong') }}</strong
                >{{ trans.get('landing.trust.item3_text_suffix') }}
              </p>
            </div>
          </div>
          <div class="trust-item">
            <div class="ic">
              <icon-clock
                :size="20"
                :stroke-width="2"
              />
            </div>
            <div>
              <h4>{{ trans.get('landing.trust.item4_title') }}</h4>
              <p>{{ trans.get('landing.trust.item4_text') }}</p>
            </div>
          </div>
        </div>

        <div class="trust-more">
          <a
            href="so-funktioniert#sicherheit"
            class="more-link"
            data-ga-content-link
            data-ga-section="security"
            >{{ trans.get('landing.trust.more_link') }}</a
          >
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import IconSafeHeart from '../icons/IconSafeHeart.vue';
import IconLockOutline from '../icons/IconLockOutline.vue';
import IconCheck from '../icons/IconCheck.vue';
import IconCheckCircle from '../icons/IconCheckCircle.vue';
import IconLockClosed from '../icons/IconLockClosed.vue';
import IconLockOpen from '../icons/IconLockOpen.vue';
import IconClock from '../icons/IconClock.vue';
import IconLockBadge from '../icons/IconLockBadge.vue';

export default {
  name: 'TrustSection',
  components: {
    IconSafeHeart,
    IconLockOutline,
    IconCheck,
    IconCheckCircle,
    IconLockClosed,
    IconLockOpen,
    IconClock,
    IconLockBadge,
  },
  data() {
    return { photoRevealed: false };
  },
};
</script>
<style scoped lang="scss">
.trust-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.trust-vis {
  position: relative;
  height: 480px;
  background: radial-gradient(circle at 50% 50%, #fff 0%, #fff5f0 60%, transparent 100%);
  border-radius: 32px;
  display: grid;
  place-items: center;
}

.trust-vis .badge-float {
  position: absolute;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 14px 32px -12px rgba(26, 12, 20, 0.2);
  font-size: 12px;
  font-weight: 600;
}

.trust-vis .b1 {
  top: 40px;
  left: 30px;
  animation: bob 5s ease-in-out infinite;
}

.trust-vis .b2 {
  bottom: 60px;
  right: 20px;
  animation: bob 6s ease-in-out infinite -2s;
}

.trust-vis .b3 {
  top: 50%;
  right: -10px;
  animation: bob 5.5s ease-in-out infinite -3s;
}

.trust-vis .badge-float .ic {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffd2c2, #ff8a9e);
  display: grid;
  place-items: center;
  color: #fff;
}

.trust-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 32px;
}

.trust-item {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 18px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 18px;
}

.trust-item .ic {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(241, 124, 8, 0.12), rgba(198, 62, 124, 0.12));
  color: var(--pink);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.trust-item h4 {
  margin: 0 0 4px;
  font-family: var(--display);
  font-size: 16px;
  font-weight: 700;
}

.trust-item p {
  margin: 0;
  font-size: 13px;
  color: var(--ink-dim);
  line-height: 1.5;
}

.safe-heart {
  position: relative;
  width: 240px;
  height: 240px;
  filter: drop-shadow(0 26px 50px rgba(198, 62, 124, 0.4));
  animation: beat 3.6s ease-in-out infinite;
}

.sh-heart {
  width: 240px;
  height: 240px;
  display: block;
}

.sh-lock {
  position: absolute;
  top: 47%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 80px;
}

.sh-body {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 62px;
  height: 46px;
  background: #fff;
  border-radius: 13px;
  box-shadow: 0 8px 18px -6px rgba(0, 0, 0, 0.28);
  z-index: 2;
  display: grid;
  place-items: center;
}

.sh-keyhole {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--pink);
  position: relative;
}

.sh-keyhole::after {
  content: '';
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 12px;
  border-radius: 0 0 2px 2px;
  background: var(--pink);
}

.sh-shackle {
  position: absolute;
  left: 50%;
  bottom: 38px;
  margin-left: -19px;
  width: 38px;
  height: 32px;
  border: 7px solid #fff;
  border-bottom: none;
  border-radius: 19px 19px 0 0;
  z-index: 1;
  animation: shClose 4.2s ease-in-out infinite;
}

.trust-item .privacy-ic {
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  background: transparent;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 6px 14px -4px rgba(20, 5, 15, 0.18), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.25s ease;
}

.trust-item .privacy-ic:hover {
  transform: scale(1.06);
}

.trust-item .privacy-ic:focus-visible {
  outline: 2px solid var(--pink);
  outline-offset: 2px;
}

.privacy-ic .pi-photos {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  filter: blur(4px) saturate(1.1);
  transition: filter 0.5s ease;
}

.privacy-ic .pi-photos > div {
  background-size: cover;
  background-position: center;
}

.privacy-ic .pi-veil {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    rgba(20, 5, 15, 0.12) 0%,
    rgba(20, 5, 15, 0.55) 100%
  );
  transition: opacity 0.5s ease;
}

.privacy-ic .pi-lock {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #fff;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.privacy-ic .pi-lock-closed {
  animation: piHint 2.4s ease-in-out infinite;
}

.privacy-ic .pi-lock-open {
  opacity: 0;
  transform: scale(0.6);
}

.privacy-ic.revealed .pi-photos {
  filter: none;
}

.privacy-ic.revealed .pi-veil {
  opacity: 0;
}

.privacy-ic.revealed .pi-lock-closed {
  opacity: 0;
  transform: scale(0.6);
  animation: none;
}

.privacy-ic.revealed .pi-lock-open {
  opacity: 1;
  transform: scale(1);
}

.badge-float .ic.ic--encrypted {
  background: linear-gradient(135deg, #34d399, #16a34a);
  color: #fff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 4px 10px -2px rgba(22, 163, 74, 0.4);
}

.badge-float .ic.ic--privacy {
  background: none;
  overflow: hidden;
  position: relative;
}

.badge-float .ic--privacy .ic-bg {
  position: absolute;
  inset: 0;
  background-image: image-set(
    url('../../../../../images/viluu/landing-v2/profiles/lena.webp') type('image/webp'),
    url('../../../../../images/viluu/landing-v2/profiles/lena.png')
  );
  background-size: cover;
  background-position: center;
  filter: blur(2.5px) saturate(1.1);
}

.badge-float .ic--privacy .ic-veil {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, rgba(20, 5, 15, 0.12), rgba(20, 5, 15, 0.5));
}

.badge-float .ic--privacy .ic-lock {
  position: relative;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55));
}

.trust-item .privacy-ic .pi-photo {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.pi-photo--lena {
  background-image: image-set(
    url('../../../../../images/viluu/landing-v2/profiles/lena.webp') type('image/webp'),
    url('../../../../../images/viluu/landing-v2/profiles/lena.png')
  );
}

.pi-photo--sophia {
  background-image: image-set(
    url('../../../../../images/viluu/landing-v2/profiles/sophia-stories.webp') type('image/webp'),
    url('../../../../../images/viluu/landing-v2/profiles/sophia-stories.png')
  );
}

.pi-photo--man1 {
  background-image: image-set(
    url('../../../../../images/viluu/landing-v2/profiles/man-1.webp') type('image/webp'),
    url('../../../../../images/viluu/landing-v2/profiles/man-1.png')
  );
}

.pi-photo--mia {
  background-image: image-set(
    url('../../../../../images/viluu/landing-v2/profiles/mia.webp') type('image/webp'),
    url('../../../../../images/viluu/landing-v2/profiles/mia.png')
  );
}

.trust-more {
  margin-top: 24px;
}

@media (prefers-reduced-motion: reduce) {
  .safe-heart {
    animation: none;
  }
  .sh-shackle {
    animation: none;
    transform: translateY(0);
  }
  .trust-vis .badge-float {
    animation: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .privacy-ic .pi-lock-closed {
    animation: none;
  }
}

@media (max-width: 1100px) {
  .trust-grid {
    grid-template-columns: 1fr;
    gap: 50px;
  }
}

@keyframes shClose {
  0%,
  16% {
    transform: translateY(-12px);
  }

  34% {
    transform: translateY(3px);
  }

  40%,
  88% {
    transform: translateY(0);
  }

  100% {
    transform: translateY(-12px);
  }
}

@keyframes piHint {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.12);
  }
}
</style>
